import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Ban,
  Archive,
  CalendarCog,
  CalendarPlus,
  History,
  KeyRound,
  Users,
  CheckCircle2,
  GitBranch,
  LoaderCircle,
  LogOut,
  ShieldAlert,
  Plus,
  RefreshCw,
  Store,
  TriangleAlert,
} from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge, EmptyState, Modal, Select } from '@/components/ui/primitives'
import { OctaPosMark } from '@/components/OctaPosMark'
import { ThemeToggle } from '@/components/ThemeToggle'
import { usePlatformSession } from '@/stores/platformSession'
import { ArchiveDialog } from '@/pages/platform/ArchiveDialog'
import { ChangePasswordDialog } from '@/pages/platform/ChangePasswordDialog'

interface TenantSummary {
  id: number
  code: string
  storeName: string
  databaseName: string
  maxBranches: number
  status: 'Active' | 'Suspended' | 'Cancelled'
  vatMode: 'Inclusive' | 'Exclusive'
  defaultVatRate: number
  expiresAtUtc: string | null
  schemaVersion: string | null
  createdAtUtc: string
  /** Years of raw detail kept before any may be archived. Operator-set. */
  retentionYears: number
  /** Active branches in use. Null when that tenant's database could not be reached. */
  branchesUsed: number | null
  /**
   * Migrations waiting to be applied to this store. Null means the database could not be read,
   * which is not the same as none — it must never render as up to date.
   */
  pendingMigrations: number | null
}

interface CreateTenantResult {
  tenantId: number
  code: string
  databaseName: string
  /** Shown exactly once — the server keeps no copy and cannot produce it again. */
  ownerUsername: string
  ownerTemporaryPassword: string
}

interface Draft {
  code: string
  storeName: string
  maxBranches: string
  vatMode: 'Inclusive' | 'Exclusive'
  vatPercent: string
  initialLbpRate: string
  ownerName: string
  ownerEmail: string
  contactPhone: string
  primaryBranchName: string
}

const empty: Draft = {
  code: '',
  storeName: '',
  maxBranches: '1',
  vatMode: 'Inclusive',
  vatPercent: '11',
  initialLbpRate: '',
  ownerName: '',
  ownerEmail: '',
  contactPhone: '',
  primaryBranchName: '',
}

const STATUS_TONE = {
  Active: 'success',
  Suspended: 'warn',
  Cancelled: 'danger',
} as const

/**
 * Adds whole calendar months to a date, clamped to the last real day of the resulting month.
 *
 * `Date.setMonth` alone rolls a nonexistent day into the next month — 31 Jan + 1 month becomes
 * 3 Mar, not 28 Feb — which would quietly push someone's renewal date later every time it lands
 * near a month-end. Billing dates are the one place that drift is worth writing a few extra
 * lines to avoid.
 */
/**
 * The last moment of a chosen day, in UTC.
 *
 * A license "until the 30th" has to include the 30th. Storing midnight would end it as the day
 * began, which is a day short of what both the operator setting it and the shopkeeper reading it
 * understand by the word.
 */
function endOfDayUtc(yyyyMmDd: string): Date {
  const [year = 1970, month = 1, day = 1] = yyyyMmDd.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day, 23, 59, 59))
}

function addMonthsClamped(date: Date, months: number): Date {
  const result = new Date(date.getTime())
  const day = result.getUTCDate()
  result.setUTCDate(1)
  result.setUTCMonth(result.getUTCMonth() + months)
  const lastDayOfTargetMonth = new Date(
    Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0),
  ).getUTCDate()
  result.setUTCDate(Math.min(day, lastDayOfTargetMonth))
  return result
}

/**
 * Create a store, and everything that comes with one: the database, the schema, the seed data,
 * and an owner account whose password is issued exactly once.
 */
export function PlatformDashboard() {
  const queryClient = useQueryClient()
  const session = usePlatformSession((state) => state.session)
  const logout = usePlatformSession((state) => state.logout)

  const [passwordOpen, setPasswordOpen] = useState(false)

  // A password that is still the one written to the startup log holds the panel until it is
  // replaced. Driven off the session rather than local state so a reload cannot step past it.
  const mustChange = session?.mustChangePassword ?? false

  const [isCreateOpen, setCreateOpen] = useState(false)
  const [draft, setDraft] = useState<Draft>(empty)
  const [createError, setCreateError] = useState<string | null>(null)
  /** The credentials just issued for a new tenant — shown once, in their own modal. */
  const [issued, setIssued] = useState<CreateTenantResult | null>(null)
  /** The tenant whose active/suspended status is being confirmed. */
  const [statusTarget, setStatusTarget] = useState<TenantSummary | null>(null)
  /** How many months to add, per tenant row — keyed so each row keeps its own draft. */
  const [monthsById, setMonthsById] = useState<Record<number, string>>({})

  /** The store whose branch cap is being changed, and the figure being typed. */
  const [capTarget, setCapTarget] = useState<TenantSummary | null>(null)
  /**
   * Years of detail this store keeps.
   *
   * Set here and nowhere the shop can reach: it governs how much of their own history is at risk,
   * so it belongs to whoever holds the delete button. Changing it deletes nothing by itself.
   */
  const [retentionDraft, setRetentionDraft] = useState('3')
  /** The store whose license date is being set by hand. */
  const [dateTarget, setDateTarget] = useState<TenantSummary | null>(null)
  /** What the last summary rebuild covered, shown once so the operator knows it did something. */
  const [rebuilt, setRebuilt] = useState<{ code: string; branchDaysRebuilt: number } | null>(null)
  /** The store whose archive is being looked at. */
  const [archiveTarget, setArchiveTarget] = useState<TenantSummary | null>(null)
  /** The store whose accounts are being looked at. */
  const [usersTarget, setUsersTarget] = useState<TenantSummary | null>(null)
  /** The date in the dialog, as yyyy-mm-dd. Empty means no expiry at all. */
  const [dateDraft, setDateDraft] = useState('')
  const [dateError, setDateError] = useState<string | null>(null)
  const [capDraft, setCapDraft] = useState('1')
  const [capError, setCapError] = useState<string | null>(null)

  const tenants = useQuery({
    queryKey: ['platform', 'tenants'],
    queryFn: () => apiClient.get<TenantSummary[]>('/api/platform/tenants'),
  })

  const create = useMutation({
    mutationFn: () =>
      apiClient.post<CreateTenantResult>('/api/platform/tenants', {
        code: draft.code.trim().toUpperCase(),
        storeName: draft.storeName.trim(),
        maxBranches: Number(draft.maxBranches || 1),
        vatMode: draft.vatMode,
        vatRate: Number(draft.vatPercent || 0) / 100,
        initialLbpRate: Number(draft.initialLbpRate || 0),
        ownerName: draft.ownerName.trim(),
        ownerEmail: draft.ownerEmail.trim(),
        contactPhone: draft.contactPhone.trim() || null,
        primaryBranchName: draft.primaryBranchName.trim() || null,
        expiresAtUtc: null,
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] })
      setCreateOpen(false)
      setDraft(empty)
      // Shown in its own modal, once the creation modal is already closed — the same shape as
      // resetting a user's password, and for the same reason: a password issued while another
      // dialog is still up is the easiest way to lose one unread.
      setIssued(result)
    },
    onError: (caught) =>
      setCreateError(caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not create the store.'),
  })

  const setStatus = useMutation({
    mutationFn: (vars: { id: number; status: 'Active' | 'Suspended' }) =>
      apiClient.patch(`/api/platform/tenants/${vars.id}`, { status: vars.status }),
    onSuccess: async () => {
      setStatusTarget(null)
      await queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] })
    },
  })

  const upgrade = useMutation({
    mutationFn: (id: number) => apiClient.post(`/api/platform/tenants/${id}/upgrade`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] }),
  })

  // Which row is actually migrating. A mutation's own isPending is per-mutation, not per-row, so
  // one upgrade spun every store's icon and disabled every button on the screen.
  const upgradingId = upgrade.isPending ? upgrade.variables : null

  /**
   * Rebuilds a shop's whole daily summary history, rather than the trailing week the hourly job
   * re-cuts.
   *
   * Needed once per store after the archive migration: the VAT bands and monthly product totals it
   * adds can only be filled in while the sale lines are still there, and they are precisely what has
   * to outlive those lines. Walks every day the shop has traded, so it takes a moment on an old one.
   */
  const rebuild = useMutation({
    mutationFn: (id: number) =>
      apiClient.post<{ code: string; branchDaysRebuilt: number }>(
        `/api/platform/tenants/${id}/rebuild-summaries`,
        {},
      ),
    onSuccess: (result) => setRebuilt(result),
  })

  /**
   * Extends a tenant's license. Anchored to whichever is later, its current expiry or now — a
   * store paying while still in credit stacks the new months on top of what is left, and a
   * lapsed one gets the full period starting from today rather than from a date already past.
   *
   * Deliberately leaves `Status` alone: expiry and status are checked independently by the
   * server (`AuthenticationService.EnsureTenantCanLogIn`), so adding months to a store a manager
   * suspended by hand does not quietly let them back in — that still needs its own Reactivate.
   */
  const extend = useMutation({
    mutationFn: (vars: { tenant: TenantSummary; months: number }) => {
      const now = new Date()
      const current = vars.tenant.expiresAtUtc ? new Date(vars.tenant.expiresAtUtc) : null
      const anchor = current && current > now ? current : now
      const next = addMonthsClamped(anchor, vars.months)
      return apiClient.patch(`/api/platform/tenants/${vars.tenant.id}`, {
        expiresAtUtc: next.toISOString(),
      })
    },
    onSuccess: async (_data, vars) => {
      setMonthsById((current) => ({ ...current, [vars.tenant.id]: '' }))
      await queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] })
    },
  })

  /**
   * Changes a store's branch ceiling after the fact — a shop that opens a second location, or
   * one moved onto a different plan.
   *
   * Lowering it below what the shop already runs is allowed and deliberately does not close any
   * branch: shutting a trading location from a billing screen would take a till offline mid-day
   * and orphan its open shift. It stops them adding another, and the modal says so plainly so
   * that is a decision rather than a surprise.
   */
  const setCap = useMutation({
    mutationFn: (vars: { id: number; maxBranches: number; retentionYears: number }) =>
      apiClient.patch(`/api/platform/tenants/${vars.id}`, {
        maxBranches: vars.maxBranches,
        retentionYears: vars.retentionYears,
      }),
    onSuccess: async () => {
      setCapTarget(null)
      await queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] })
    },
    onError: (caught) =>
      setCapError(
        caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not change the cap.',
      ),
  })

  /**
   * Writes a license date chosen by hand, or removes it.
   *
   * Set to the very end of the chosen day, so "expires on the 30th" means the store trades all
   * of the 30th. Anchoring to midnight would cut them off the moment the date arrived, a day
   * earlier than the operator and the shopkeeper both read it.
   */
  const setExpiry = useMutation({
    mutationFn: (vars: { id: number; date: string }) =>
      apiClient.patch(`/api/platform/tenants/${vars.id}`, {
        expiresAtUtc: vars.date === '' ? null : endOfDayUtc(vars.date).toISOString(),
        clearExpiry: vars.date === '',
      }),
    onSuccess: async () => {
      setDateTarget(null)
      await queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] })
    },
    onError: (caught) =>
      setDateError(
        caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not set that date.',
      ),
  })

  function openDate(tenant: TenantSummary) {
    setDateTarget(tenant)
    setDateDraft(tenant.expiresAtUtc ? tenant.expiresAtUtc.slice(0, 10) : '')
    setDateError(null)
  }

  function openCap(tenant: TenantSummary) {
    setCapTarget(tenant)
    setCapDraft(String(tenant.maxBranches))
    setRetentionDraft(String(tenant.retentionYears))
    setCapError(null)
  }

  function openCreate() {
    setDraft(empty)
    setCreateError(null)
    setCreateOpen(true)
  }

  return (
    <div className="min-h-dvh">
      <header className="surface-raised flex items-center justify-between border-b px-4 py-3 sm:px-6">
        <div className="flex items-center gap-2">
          <OctaPosMark title={null} className="h-6 w-6 text-[var(--accent)]" />
          <span className="font-semibold">OctaPOS Platform</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden text-sm text-[var(--text-secondary)] sm:inline">
            {session?.displayName}
          </span>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Change your password"
            title="Change your password"
            onClick={() => setPasswordOpen(true)}
          >
            <KeyRound className="h-4 w-4" />
          </Button>
          <ThemeToggle />
          <Button variant="ghost" size="icon" aria-label="Sign out" onClick={logout}>
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6">
        {/* Not forced for an account that predates this feature — locking the only operator out of
            the only panel that can unlock anything is not a trade worth making on a deploy. Said
            out loud instead, because the password it is still on was written to a log file. */}
        {session && !session.mustChangePassword && session.passwordChangedAtUtc === null && (
          <div className="mb-5 flex items-start gap-2 rounded-[var(--radius-card)] border border-warn-500/40 bg-warn-500/10 p-4">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-warn-500" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">This password has never been changed.</p>
              <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
                It was generated when the platform first started and written to the server log.
                Anyone who can read that log can sign in here.
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => setPasswordOpen(true)}>
              Change it
            </Button>
          </div>
        )}

        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">Stores</h1>
            <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
              Every tenant, each in its own database.
            </p>
          </div>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4" />
            New store
          </Button>
        </div>

        {tenants.data?.length === 0 ? (
          <EmptyState icon={Store} title="No stores yet" description="Create the first one." />
        ) : (
          <ul className="flex flex-col gap-2">
            {tenants.data?.map((tenant) => {
              const expiry = tenant.expiresAtUtc ? new Date(tenant.expiresAtUtc) : null
              const isExpired = expiry !== null && expiry <= new Date()
              const months = monthsById[tenant.id] ?? ''

              return (
              <li
                key={tenant.id}
                className="surface-raised flex flex-wrap items-center gap-3 rounded-[var(--radius-card)] border p-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{tenant.storeName}</span>
                    <Badge tone="neutral" className="tabular">
                      {tenant.code}
                    </Badge>
                    <Badge tone={STATUS_TONE[tenant.status]}>{tenant.status}</Badge>
                    <Badge tone={isExpired ? 'danger' : 'neutral'}>
                      {expiry
                        ? `${isExpired ? 'Expired' : 'Licensed until'} ${expiry.toLocaleDateString()}`
                        : 'No expiry'}
                    </Badge>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-[var(--text-secondary)]">
                    {/* Usage first, cap second — the pair is what a billing decision is made
                        on, and the cap alone says nothing about whether it is being outgrown. */}
                    {tenant.branchesUsed === null
                      ? `Up to ${tenant.maxBranches} ${tenant.maxBranches === 1 ? 'branch' : 'branches'}`
                      : `${tenant.branchesUsed} of ${tenant.maxBranches} ${
                          tenant.maxBranches === 1 ? 'branch' : 'branches'
                        } used`}{' '}
                    · VAT {tenant.vatMode} at {(tenant.defaultVatRate * 100).toFixed(1)}%
                    {tenant.schemaVersion ? ` · schema ${tenant.schemaVersion.slice(0, 15)}` : ''}
                  </p>
                </div>

                {/* Extends the license by however many months were paid for. Anchored to the
                    later of today or the current expiry — see the `extend` mutation. */}
                <div className="flex shrink-0 items-center gap-1.5">
                  <Input
                    type="number"
                    inputMode="numeric"
                    value={months}
                    onChange={(event) =>
                      setMonthsById((current) => ({ ...current, [tenant.id]: event.target.value }))
                    }
                    placeholder="1"
                    aria-label={`Months to add to or take off ${tenant.storeName}`}
                    className="tabular w-16 text-center"
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={extend.isPending || Number(months || 0) === 0}
                    onClick={() => extend.mutate({ tenant, months: Number(months || 0) })}
                    title="Add months to the license — a negative number takes them off"
                  >
                    <CalendarPlus className="h-4 w-4" />
                    Apply
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Set the license date for ${tenant.storeName}`}
                    onClick={() => openDate(tenant)}
                    title="Set the license date exactly"
                  >
                    <CalendarCog className="h-4 w-4" />
                  </Button>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Accounts in ${tenant.storeName}`}
                  onClick={() => setUsersTarget(tenant)}
                  title="Accounts and passwords"
                >
                  <Users className="h-4 w-4" />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Change the limits for ${tenant.storeName}`}
                  onClick={() => openCap(tenant)}
                  title="Branch cap and retention"
                >
                  <GitBranch className="h-4 w-4" />
                </Button>

                {/* Tenant databases are upgraded deliberately rather than on deploy, so after a
                    release this list is a row of identical buttons with no way to tell which
                    shops are behind. Red means this one has migrations waiting; the count is on
                    the tooltip. A database that could not be read stays plain rather than
                    claiming to be up to date — unknown is not the same as none. */}
                <Button
                  variant="ghost"
                  size="icon"
                  className={
                    tenant.pendingMigrations
                      ? 'text-danger-500 bg-danger-500/10 hover:bg-danger-500/20'
                      : undefined
                  }
                  aria-label={
                    tenant.pendingMigrations
                      ? `Apply ${tenant.pendingMigrations} pending migration${tenant.pendingMigrations === 1 ? '' : 's'} to ${tenant.storeName}`
                      : `Apply pending migrations to ${tenant.storeName}`
                  }
                  disabled={upgradingId === tenant.id}
                  onClick={() => upgrade.mutate(tenant.id)}
                  title={
                    tenant.pendingMigrations === null
                      ? 'Apply pending migrations — this database could not be read'
                      : tenant.pendingMigrations === 0
                        ? 'Up to date'
                        : `${tenant.pendingMigrations} migration${tenant.pendingMigrations === 1 ? '' : 's'} pending`
                  }
                >
                  <RefreshCw
                    className={upgradingId === tenant.id ? 'h-4 w-4 animate-spin' : 'h-4 w-4'}
                  />
                </Button>

                {/* Safe to press at any time — it only recomputes derived rows, and never touches
                    a day whose detail has been archived. */}
                {/* Export only. The prune is its own work and its own confirmation. */}
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Archive old detail for ${tenant.storeName}`}
                  onClick={() => setArchiveTarget(tenant)}
                  title={`Archive — keeps ${tenant.retentionYears} years`}
                >
                  <Archive className="h-4 w-4" />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Rebuild summary history for ${tenant.storeName}`}
                  disabled={rebuild.isPending}
                  onClick={() => rebuild.mutate(tenant.id)}
                  title="Rebuild summary history"
                >
                  <History className={rebuild.isPending ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
                </Button>

                {tenant.status === 'Active' ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Suspend ${tenant.storeName}`}
                    onClick={() => setStatusTarget(tenant)}
                  >
                    <Ban className="h-4 w-4" />
                  </Button>
                ) : tenant.status === 'Suspended' ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Reactivate ${tenant.storeName}`}
                    onClick={() => setStatus.mutate({ id: tenant.id, status: 'Active' })}
                  >
                    <CheckCircle2 className="h-4 w-4" />
                  </Button>
                ) : null}
              </li>
              )
            })}
          </ul>
        )}
      </main>

      {/* The branch ceiling, changeable after the store was created — a shop opening a second
          location should not need a new database. */}
      {/* Says what it did. A rebuild that silently changes nothing on screen reads as a dead
          button, and this one is meant to be pressed once per store and then forgotten. */}
      <Modal
        open={rebuilt !== null}
        onClose={() => setRebuilt(null)}
        title="Summary history rebuilt"
        footer={<Button onClick={() => setRebuilt(null)}>Done</Button>}
      >
        <p className="text-sm">
          Rebuilt <span className="tabular font-semibold">{rebuilt?.branchDaysRebuilt}</span>
          {' '}branch-days for {rebuilt?.code}. Days whose detail has already been archived were
          left alone.
        </p>
      </Modal>

      <ChangePasswordDialog
        open={passwordOpen || mustChange}
        forced={mustChange}
        // Held open by hand once it has succeeded: the change clears mustChange, which was the
        // only thing keeping a forced dialog mounted, so the confirmation would flash past.
        onChanged={() => setPasswordOpen(true)}
        onClose={() => setPasswordOpen(false)}
      />

      <ArchiveDialog
        tenantId={archiveTarget?.id ?? null}
        storeName={archiveTarget?.storeName ?? ''}
        onClose={() => setArchiveTarget(null)}
      />

      <TenantUsersDialog tenant={usersTarget} onClose={() => setUsersTarget(null)} />

      <Modal
        open={dateTarget !== null}
        onClose={() => setDateTarget(null)}
        title={`License date — ${dateTarget?.storeName ?? ''}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDateTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => setExpiry.mutate({ id: dateTarget!.id, date: dateDraft })}
              disabled={setExpiry.isPending}
            >
              {setExpiry.isPending ? 'Saving…' : 'Save date'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-[var(--text-secondary)]">
            {dateTarget?.expiresAtUtc
              ? `Currently licensed until ${new Date(dateTarget.expiresAtUtc).toLocaleDateString()}.`
              : 'This store currently has no expiry date.'}
          </p>

          <Input
            label="Licensed until"
            type="date"
            value={dateDraft}
            onChange={(event) => setDateDraft(event.target.value)}
            className="tabular text-lg"
            hint="The store trades for the whole of this day. Clear it for a store with no end date."
            autoFocus
          />

          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => setDateDraft('')}>
              No expiry
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setDateDraft(new Date().toISOString().slice(0, 10))}
            >
              Today
            </Button>
          </div>

          {/* Allowed on purpose — cutting a store off is a real thing an operator does — but
              said out loud, because a date typed a year wrong looks exactly the same. */}
          {dateDraft !== '' && endOfDayUtc(dateDraft) < new Date() && (
            <p className="flex items-start gap-2 rounded-lg bg-warn-500/10 px-3 py-2 text-sm text-warn-500">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
              That date has already passed, so this store will be locked out at the next sign-in.
              Anyone already signed in keeps working until their session ends.
            </p>
          )}

          {dateError && (
            <p role="alert" className="rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
              {dateError}
            </p>
          )}
        </div>
      </Modal>

      <Modal
        open={capTarget !== null}
        onClose={() => setCapTarget(null)}
        title={`Limits — ${capTarget?.storeName ?? ''}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCapTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                setCap.mutate({
                  id: capTarget!.id,
                  maxBranches: Number(capDraft || 0),
                  retentionYears: Number(retentionDraft || 0),
                })
              }
              disabled={
                setCap.isPending || Number(capDraft || 0) < 1 || Number(retentionDraft || 0) < 1
              }
            >
              {setCap.isPending ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Input
            label="Maximum branches"
            type="number"
            inputMode="numeric"
            min="1"
            value={capDraft}
            onChange={(event) => setCapDraft(event.target.value)}
            className="tabular text-lg"
            hint={
              capTarget?.branchesUsed === null || capTarget === null
                ? 'This store is not reachable, so how many it is running could not be read.'
                : `Currently running ${capTarget.branchesUsed} ${
                    capTarget.branchesUsed === 1 ? 'branch' : 'branches'
                  }.`
            }
            autoFocus
          />

          {/* Beside the cap because both are limits only the operator sets. This one governs how
              much of a shop's history can ever be deleted, which is not a decision to hand to the
              shop — and the floor of one year is because nothing time-limits a refund, so an old
              sale can still be handed back and needs its lines. */}
          <Input
            label="Years of detail kept"
            type="number"
            inputMode="numeric"
            min="1"
            value={retentionDraft}
            onChange={(event) => setRetentionDraft(event.target.value)}
            className="tabular text-lg"
            hint="Before any of it may be archived and deleted. Changing this deletes nothing on its own — an archive still has to be run by hand."
          />

          {/* Allowed, but said out loud. The server enforces the cap on the next branch a shop
              tries to add; it never closes one that already exists. */}
          {capTarget?.branchesUsed != null && Number(capDraft || 0) < capTarget.branchesUsed && (
            <p className="flex items-start gap-2 rounded-lg bg-warn-500/10 px-3 py-2 text-sm text-warn-500">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
              This store already runs {capTarget.branchesUsed}. Nothing closes — they keep trading
              from all of them and simply cannot add another until the cap is raised again.
            </p>
          )}

          {capError && (
            <p role="alert" className="rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
              {capError}
            </p>
          )}
        </div>
      </Modal>

      {/* Confirms before suspending — billing lapsing shows every user of that shop a notice
          and puts the till read-only, which is not something a stray click should do. */}
      <Modal
        open={statusTarget !== null}
        onClose={() => setStatusTarget(null)}
        title={`Suspend ${statusTarget?.storeName ?? ''}?`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setStatusTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={setStatus.isPending}
              onClick={() => setStatus.mutate({ id: statusTarget!.id, status: 'Suspended' })}
            >
              {setStatus.isPending ? 'Suspending…' : 'Suspend'}
            </Button>
          </>
        }
      >
        <p className="text-sm">
          Every user at this store can still sign in to see a notice, but the till goes
          read-only. Reactivating it afterwards restores it exactly as it was.
        </p>
      </Modal>

      {/* The issued credentials, in their own modal so a reset from the row cannot lose it —
          shown once, exactly like resetting a user's password. */}
      <Modal
        open={issued !== null}
        onClose={() => setIssued(null)}
        title="Store created"
        footer={<Button onClick={() => setIssued(null)}>Done</Button>}
      >
        <p className="text-sm">
          Write this down now. The password is not stored anywhere and cannot be shown again.
        </p>
        <dl className="surface-sunken mt-3 flex flex-col gap-2 rounded-lg px-4 py-3 text-sm">
          <div className="flex items-center justify-between gap-3">
            <dt className="text-[var(--text-secondary)]">Store code</dt>
            <dd className="tabular font-semibold tracking-widest">{issued?.code}</dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="text-[var(--text-secondary)]">Owner username</dt>
            <dd className="tabular font-semibold">{issued?.ownerUsername}</dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="text-[var(--text-secondary)]">Owner password</dt>
            <dd className="tabular text-right text-lg font-semibold tracking-wider">
              {issued?.ownerTemporaryPassword}
            </dd>
          </div>
        </dl>
        <p className="mt-3 text-xs text-[var(--text-secondary)]">
          They will sign in with the store code and username above, and this password — and be
          asked to change it on first sign-in.
        </p>
      </Modal>

      {/* Creating a store. */}
      <Modal
        open={isCreateOpen}
        onClose={() => setCreateOpen(false)}
        wide
        title="New store"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => create.mutate()}
              disabled={
                create.isPending ||
                !draft.code.trim() ||
                !draft.storeName.trim() ||
                !draft.ownerName.trim() ||
                !draft.ownerEmail.trim()
              }
            >
              {create.isPending && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {create.isPending ? 'Creating…' : 'Create store'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Store code"
              value={draft.code}
              onChange={(event) => setDraft((d) => ({ ...d, code: event.target.value.toUpperCase() }))}
              placeholder="ACME"
              hint="3–12 characters, A–Z and 0–9 only. This is what a cashier types to sign in."
              autoCapitalize="characters"
              spellCheck={false}
              className="tabular tracking-widest uppercase"
              required
            />
            <Input
              label="Store name"
              value={draft.storeName}
              onChange={(event) => setDraft((d) => ({ ...d, storeName: event.target.value }))}
              placeholder="Acme Market"
              required
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Input
              label="Branch limit"
              type="number"
              inputMode="numeric"
              min="1"
              value={draft.maxBranches}
              onChange={(event) => setDraft((d) => ({ ...d, maxBranches: event.target.value }))}
            />
            <Select
              label="VAT"
              value={draft.vatMode}
              onChange={(event) =>
                setDraft((d) => ({ ...d, vatMode: event.target.value as Draft['vatMode'] }))
              }
            >
              <option value="Inclusive">Inclusive — shelf price includes it</option>
              <option value="Exclusive">Exclusive — added at checkout</option>
            </Select>
            <Input
              label="VAT rate %"
              type="number"
              inputMode="decimal"
              step="0.1"
              min="0"
              value={draft.vatPercent}
              onChange={(event) => setDraft((d) => ({ ...d, vatPercent: event.target.value }))}
            />
          </div>

          {/* Said plainly, because the two fields above read as switching VAT on. */}
          <p className="text-xs text-[var(--text-secondary)]">
            The store starts <span className="font-medium">not VAT registered</span> — most are
            below the threshold, and charging it without being registered is the harder mistake
            to undo. These settings are kept ready, so turning it on later is one switch in
            Settings.
          </p>

          <Input
            label="Starting LBP rate"
            type="number"
            inputMode="numeric"
            min="0"
            value={draft.initialLbpRate}
            onChange={(event) => setDraft((d) => ({ ...d, initialLbpRate: event.target.value }))}
            placeholder="Leave blank for the default (89,000)"
            hint="The shop can move its own rate from Settings once it opens."
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Owner name"
              value={draft.ownerName}
              onChange={(event) => setDraft((d) => ({ ...d, ownerName: event.target.value }))}
              placeholder="Full name"
              required
            />
            {/* The store's billing contact, kept on the tenant record here in the control
                database. It is not copied into the shop — staff accounts hold no email — but the
                owner's first username is built from it, so it is still worth getting right. */}
            <Input
              label="Owner email"
              type="email"
              value={draft.ownerEmail}
              onChange={(event) => setDraft((d) => ({ ...d, ownerEmail: event.target.value }))}
              placeholder="owner@store.com"
              hint="Billing contact for the store. Their username is built from it."
              required
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Contact phone"
              value={draft.contactPhone}
              onChange={(event) => setDraft((d) => ({ ...d, contactPhone: event.target.value }))}
              placeholder="Optional"
            />
            <Input
              label="First branch name"
              value={draft.primaryBranchName}
              onChange={(event) => setDraft((d) => ({ ...d, primaryBranchName: event.target.value }))}
              placeholder="Defaults to the store name"
            />
          </div>

          {createError && (
            <p role="alert" className="rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
              <TriangleAlert className="mr-1 inline h-4 w-4" />
              {createError}
            </p>
          )}
        </div>
      </Modal>
    </div>
  )
}

interface TenantUser {
  id: number
  username: string
  displayName: string
  role: string
  isActive: boolean
  mustChangePassword: boolean
  isLockedOut: boolean
  lastLoginAtUtc: string | null
}

/**
 * The accounts inside one store, and the one thing that can be done to them from here.
 *
 * Deliberately read-only apart from the password. An operator can already create and delete whole
 * databases, so this is no new power — but editing a shop's staff from outside it would be, and
 * would leave the owner unable to explain their own user list. Every reset is written into that
 * shop's own audit log under the operator's email.
 */
function TenantUsersDialog({
  tenant,
  onClose,
}: {
  tenant: TenantSummary | null
  onClose: () => void
}) {
  /** The password just issued. Shown once, in its own panel, because it is never retrievable. */
  const [issued, setIssued] = useState<{ name: string; password: string } | null>(null)
  /** The account a reset has been asked for, held until it is confirmed. */
  const [confirming, setConfirming] = useState<TenantUser | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (tenant) {
      setIssued(null)
      setConfirming(null)
      setError(null)
    }
  }, [tenant])

  const users = useQuery({
    queryKey: ['platform', 'tenant-users', tenant?.id],
    queryFn: () => apiClient.get<TenantUser[]>(`/api/platform/tenants/${tenant!.id}/users`),
    enabled: tenant !== null,
  })

  const reset = useMutation({
    mutationFn: (user: TenantUser) =>
      apiClient.post<{ temporaryPassword: string }>(
        `/api/platform/tenants/${tenant!.id}/users/${user.id}/reset-password`,
        {},
      ),
    onSuccess: (result, user) => {
      setIssued({ name: user.displayName, password: result.temporaryPassword })
      setConfirming(null)
      setError(null)
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError
          ? (caught.detail ?? caught.message)
          : 'Could not reset that password.',
      ),
  })

  return (
    <Modal
      open={tenant !== null}
      onClose={onClose}
      wide
      title={`Accounts — ${tenant?.storeName ?? ''}`}
      description="Passwords only. Everything else about these accounts is the shop's own to change."
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {/* Shown before the list, and it stays until the dialog closes: this is the only time
          anyone will ever see this password, so it must not scroll away behind a table. */}
      {issued && (
        <div className="mb-4 rounded-[var(--radius-card)] border border-warn-500/40 bg-warn-500/10 p-4">
          <p className="text-sm font-medium">New password for {issued.name}</p>
          <p className="tabular mt-2 select-all text-2xl font-semibold">{issued.password}</p>
          <p className="mt-2 text-xs text-[var(--text-secondary)]">
            Write it down now. It is generated once, never stored, and cannot be shown again. They
            will be asked to change it as soon as they sign in.
          </p>
        </div>
      )}

      {confirming && (
        <div className="mb-4 rounded-[var(--radius-card)] border border-danger-500/40 bg-danger-500/10 p-4">
          <p className="text-sm font-medium">
            Reset the password for {confirming.displayName}?
          </p>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Their current password stops working straight away, and there is no way back to it.
          </p>
          <div className="mt-3 flex gap-2">
            <Button onClick={() => reset.mutate(confirming)} disabled={reset.isPending}>
              {reset.isPending ? 'Resetting…' : 'Reset it'}
            </Button>
            <Button variant="secondary" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {error && (
        <p role="alert" className="mb-4 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
          {error}
        </p>
      )}

      {users.isLoading && <p className="text-sm text-[var(--text-secondary)]">Loading…</p>}

      {users.error && (
        <p role="alert" className="rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
          {users.error instanceof ApiError
            ? (users.error.detail ?? users.error.message)
            : 'That store could not be reached.'}
        </p>
      )}

      <ul className="flex flex-col gap-2">
        {users.data?.map((user) => (
          <li
            key={user.id}
            className="surface-raised flex flex-wrap items-center gap-3 rounded-lg border p-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="truncate text-sm font-medium">{user.displayName}</span>
                <Badge tone="neutral">{user.role}</Badge>
                {!user.isActive && <Badge tone="neutral">Disabled</Badge>}
                {user.isLockedOut && <Badge tone="danger">Locked out</Badge>}
                {user.mustChangePassword && <Badge tone="warn">Must change</Badge>}
              </div>
              {/* Both, and the username labelled: the display name is who the operator is
                  looking for, the username is what they have to read down the phone after a
                  reset. An unlabelled second line gets mistaken for one or the other. */}
              <p className="truncate text-xs text-[var(--text-secondary)]">
                Signs in as <span className="font-medium text-[var(--text-primary)]">{user.username}</span>
              </p>
            </div>

            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setIssued(null)
                setConfirming(user)
              }}
              disabled={reset.isPending}
            >
              <KeyRound className="h-4 w-4" />
              Reset password
            </Button>
          </li>
        ))}
      </ul>
    </Modal>
  )
}
